import { Injectable, inject } from '@angular/core';
import { FileOpener, FileOpenerOptions } from '@capacitor-community/file-opener';
import { Directory, Filesystem } from '@capacitor/filesystem';
import { PlatformService } from '../platform';

@Injectable({
  providedIn: 'root',
})
export class PlatformFileAdapterService {
  private readonly platform = inject(PlatformService);

  public async saveAndOpenFile(blob: Blob, filename: string): Promise<void> {
    const mime = blob.type || 'application/pdf';
    const fixedFilename = this.fixFilenameExtension(filename, mime);

    if (this.platform.isHybrid) {
      await this.saveFileToDevice(blob, fixedFilename, mime);
    } else {
      this.saveFileToBrowser(blob, fixedFilename);
    }
  }

  private saveFileToBrowser(data: Blob, filename: string): void {
    const url = window.URL.createObjectURL(data);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    a.remove();
  }

  private async saveFileToDevice(blob: Blob, filename: string, mime: string): Promise<void> {
    try {
      const base64Data = await this.convertBlobToBase64(blob);
      const base64 = base64Data.split(',')[1];

      await Filesystem.writeFile({
        path: filename,
        data: base64,
        directory: Directory.Documents,
      });

      const filePath = await Filesystem.getUri({
        directory: Directory.Documents,
        path: filename,
      });

      const fileOpenerOptions: FileOpenerOptions = {
        filePath: filePath.uri,
        contentType: mime,
        openWithDefault: true,
      };

      await FileOpener.open(fileOpenerOptions);
    } catch (error) {
      console.error('Error opening file: ', error);
      throw error;
    }
  }

  private convertBlobToBase64(blob: Blob): Promise<string> {
    return new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }

  private fixFilenameExtension(filename: string, mime: string): string {
    const extensions: Record<string, string> = {
      'image/jpeg': 'jpg',
      'application/pdf': 'pdf',
    };

    const newExtension = extensions[mime];
    if (!newExtension) {
      return filename;
    }

    const nameWithoutExtension = filename.substring(0, filename.lastIndexOf('.')) || filename;
    return `${nameWithoutExtension}.${newExtension}`;
  }
}
